import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (e) {
      // 401 here just means "not logged in" — don't pollute console with that.
      if (e?.response && e.response.status !== 401) console.error("auth check failed", e);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Listen for the sliding-session 401 dispatched by `lib/api.js`. We clear
  // the user state, show a single toast, and bounce the SPA to /admin/login
  // (or /login for customers — we don't know which, so we use the route the
  // user was on to decide).
  useEffect(() => {
    const onExpired = (e) => {
      setUser(null);
      try { toast.error(e?.detail || "Session expired. Please log in again."); } catch (_) { /* */ }
      // Use window.location so the redirect works from anywhere, including
      // outside react-router context.
      const path = window.location.pathname;
      const target = path.startsWith("/admin") ? "/admin/login" : "/login";
      if (path !== target) {
        // Defer to next tick so the toast registers before navigation.
        setTimeout(() => { window.location.assign(target); }, 50);
      }
    };
    window.addEventListener("npc:session-expired", onExpired);
    return () => window.removeEventListener("npc:session-expired", onExpired);
  }, []);

  // Activity-based session keep-alive (Feb 2026).
  //   Problem the user reported: admin opens the offline-registration form,
  //   gets called away for 20-30 min, fills + submits — system shows
  //   "session expired" because the 15-min server JWT had quietly lapsed.
  //   Fix: while the admin is actively interacting (typing, scrolling,
  //   clicking) we ping `/auth/me` every 5 min, which the backend response
  //   middleware translates into a fresh `X-Auth-Refresh` JWT — sliding the
  //   server-side expiry forward. If they truly walk away (no input for >5
  //   min) the pings pause; the 60-min server idle clock then takes over.
  //   This way: typing a long form NEVER expires; an unattended tab does.
  useEffect(() => {
    if (!user || user.role === "customer") return undefined;
    let lastActivityAt = Date.now();
    const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    const onActivity = () => { lastActivityAt = Date.now(); };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    // Tick every 4 minutes — well inside the 60-min server idle window so we
    // always refresh before expiry; and far enough apart that we don't spam
    // the API while someone is typing.
    const KEEPALIVE_MS = 4 * 60 * 1000;
    const ACTIVE_WINDOW_MS = 5 * 60 * 1000;
    const intervalId = setInterval(() => {
      // Only ping if the user has been active in the last 5 minutes — a quiet
      // tab gets to expire naturally per the server idle policy.
      if (Date.now() - lastActivityAt < ACTIVE_WINDOW_MS) {
        api.get("/auth/me").catch(() => { /* axios interceptor handles 401s */ });
      }
    }, KEEPALIVE_MS);
    return () => {
      clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
    };
  }, [user]);

  const loginWithPassword = async (email, password, isAdmin = false) => {
    const path = isAdmin ? "/auth/admin-login" : "/auth/login";
    const body = isAdmin ? { identifier: email, password } : { email, password };
    const r = await api.post(path, body);
    localStorage.setItem("vip_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  // WhatsApp OTP login helpers — additive; the existing password flow
  // remains the default. Two-step: send → verify. The verify step receives
  // the SAME `{token, user}` payload that /auth/login emits, so the rest
  // of the SPA (session refresh, role routing) needs no changes.
  const sendWhatsAppOtp = async (mobile) => {
    const r = await api.post("/auth/whatsapp/otp/send", { mobile });
    return r.data;
  };

  const loginWithWhatsAppOtp = async (mobile, otp) => {
    const r = await api.post("/auth/whatsapp/otp/verify", { mobile, otp });
    localStorage.setItem("vip_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    localStorage.setItem("vip_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("logout API call failed", e); /* proceed with local cleanup anyway */ }
    localStorage.removeItem("vip_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithPassword, sendWhatsAppOtp, loginWithWhatsAppOtp, register, logout, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
